import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet, ApiError } from '../api';
import { abbrM, money, nfmt } from '../format';
import { lbl } from '../labels';
import { Card, Cards } from '../components/Card';
import { Table } from '../components/Table';
import { Tabs } from '../components/Tabs';

/**
 * Dividends — cards, the projection table and the three breakdowns, from `/api/v1/dividends`
 * (`dividendsPage()`), which is what views/dividends.ejs renders.
 *
 * Two things deliberately differ from the EJS page, both recorded in the change's tasks: the rows do not
 * expand into the lazy history panes yet (that needs `/dividends/month|year|ticker/:key` as API routes),
 * and the cards row keeps the same five figures, so the numbers already agree.
 */
type YearTotal = { s: number; c: number };
type MonthRow = { ym: string; s: number; c: number; taxable: number };
type TickerRow = {
  ticker: string;
  payments12: number;
  t12: number;
  allTime: number;
  taxable: number;
  rocPct: number;
  yieldOnCost: number | null;
};
type ProjectionRow = { ticker: string; cadence?: number; note?: string; upcoming: number; predicted?: number };
type Projection = {
  perTicker: ProjectionRow[];
  total: number;
  ytd: number;
  projectedYear: number;
};
type DividendsPayload = {
  t12Total: number;
  thisYear: YearTotal;
  lastYear: YearTotal;
  taxableTotal12: number;
  allTime: number;
  rows: TickerRow[];
  monthly: MonthRow[];
  projection: Projection;
  window: { start: string; end: string };
};

/** One row's history, fetched when the row is expanded — the EJS panes do the same, a fragment per row. */
type HistRow = {
  tx_date: string;
  ticker: string | null;
  amount: number | null;
  shares?: number | null;
  per_share?: number | null;
  roc_pct?: number | null;
};

function History({ kind, id, open }: { kind: 'month' | 'year' | 'ticker'; id: string; open: boolean }) {
  const q = useQuery<{ rows: HistRow[] } | HistRow[]>({
    queryKey: ['div-history', kind, id],
    queryFn: () => apiGet<{ rows: HistRow[] } | HistRow[]>(`/api/v1/dividends/${kind}/${encodeURIComponent(id)}`),
    enabled: open,
  });
  const raw = q.data;
  // The three endpoints answer in two shapes (a wrapped { rows } for month/year, a bare array for a ticker);
  // both are the EJS payloads' own shapes, so the reader tolerates what the writer sends.
  const rows = Array.isArray(raw) ? raw : raw?.rows ?? [];
  // The page's pane is a paragraph until its fragment arrives, then the table — never a header over an empty
  // body, and "No payments." is a sentence on the page, not a row in a table.
  if (q.isLoading) return <p className="muted" style={{ padding: '10px 14px' }}>Loading…</p>;
  if (!rows.length) return <p className="muted t-body">No payments.</p>;
  // The pane is the page's own table, column for column (views/dividends.ejs's `table:pane-open` handler): Date,
  // then Ticker unless the row IS a ticker, then Dividend ($), Shares, Amount, ROC % and the Taxable the ROC
  // leaves. It used to be a five-column summary with Shares before Per share and no ROC/taxable at all, and it
  // was `compact` — which is the totals-strip scale, not the page's.
  const withTicker = kind !== 'ticker';
  return (
    <Table
        expandable={false}
        // The page's handler renders `{ tableClass: 'row-hover' }` — no `holdings-table`, whose rules the inner
        // table must not inherit.
        tableClass="row-hover"
        columns={[
          { label: lbl('Date') },
          ...(withTicker ? [{ label: lbl('Ticker') }] : []),
          { label: lbl('Dividend ($)'), num: true },
          { label: lbl('Shares'), num: true },
          { label: lbl('Amount'), num: true },
          { label: lbl('ROC %'), num: true },
          { label: lbl('Taxable'), num: true },
        ]}
        rows={rows.map((r, i) => {
          const roc = r.roc_pct || 0;
          const amount = r.amount;
          return {
            key: `${r.tx_date}-${r.ticker}-${i}`,
            cells: [
              r.tx_date,
              ...(withTicker ? [r.ticker ?? '—'] : []),
              r.per_share == null ? '—' : money(r.per_share),
              r.shares == null ? '—' : Number(r.shares).toLocaleString('en-US', { maximumFractionDigits: 6 }),
              money(amount),
              roc.toFixed(1) + '%',
              money(amount == null ? null : amount * (1 - roc / 100)),
            ],
          };
        })}
    />
  );
}

export function Dividends() {
  // Which row's history is expanded. Nothing opens on its own: the EJS page ships every pane closed, and a
  // row that is already tinted when the pointer arrives cannot show that it responds to the pointer.
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [tab, setTab] = useState('monthly');
  const q = useQuery<DividendsPayload, ApiError>({ queryKey: ['dividends'], queryFn: () => apiGet<DividendsPayload>('/api/v1/dividends') });

  // Grouping is presentation, not data: api/pages.js sends the months and the per-ticker rows, and both
  // front ends group them the same way — by year, newest first.
  const years = useMemo(() => {
    const monthly = q.data?.monthly ?? [];
    const by: Record<string, { y: string; c: number; taxable: number; s: number }> = {};
    for (const m of monthly) {
      const y = m.ym.slice(0, 4);
      by[y] = by[y] || { y, c: 0, taxable: 0, s: 0 };
      by[y].c += m.c;
      by[y].taxable += m.taxable;
      by[y].s += m.s;
    }
    return Object.values(by).sort((a, b) => b.y.localeCompare(a.y));
  }, [q.data]);

  if (q.isLoading) return <main>Loading…</main>;
  if (q.error) return <main className="panel-card">Could not load dividends: {q.error.message}</main>;

  const { thisYear, lastYear, taxableTotal12, allTime, rows, monthly, projection } = q.data!;
  const nowY = new Date().getFullYear();
  const posIf = (n: number) => (n > 0 ? 'pos' : '');

  return (
    <main>
      <h1>Dividends</h1>

      <Cards className="dividends-cards">
        <Card label={`Projected (${nowY})`} value={<span title={money(projection.projectedYear)}>{abbrM(projection.projectedYear)}</span>} />
        <Card label={`This year (${nowY})`} valueClass={posIf(thisYear.s)} value={<span title={money(thisYear.s)}>{abbrM(thisYear.s)}</span>} />
        <Card label={`Last year (${nowY - 1})`} valueClass={posIf(lastYear.s)} value={<span title={money(lastYear.s)}>{abbrM(lastYear.s)}</span>} />
        <Card label={`Taxable dividends (${nowY})`} valueClass={posIf(taxableTotal12)} value={<span title={money(taxableTotal12)}>{abbrM(taxableTotal12)}</span>} />
        <Card label={lbl('All time')} valueClass={posIf(allTime)} value={<span title={money(allTime)}>{abbrM(allTime)}</span>} />
      </Cards>

      <section className="panel-card" style={{ marginTop: 24 }}>
        <Table
          expandable={false}
          tableClass="holdings-table reorder-off"
          columns={[{ label: `Projected rest of ${nowY}` }, { label: lbl('Payments left'), num: true }, { label: lbl('Predicted'), num: true }]}
          rows={[
            ...projection.perTicker.map((p) => ({
              key: p.ticker,
              cells: [
                // The page's cell is the ticker and its note, not a wrapper around them: a `<span>` here is one
                // extra empty span per row in the DOM, and the parity scan counts it on every projection row.
                <Fragment key="t">
                  <strong>{p.ticker}</strong>{' '}
                  <span className="muted t-small">{p.cadence ? `${p.cadence}d cadence` : p.note}</span>
                </Fragment>,
                p.upcoming,
                p.predicted ? money(p.predicted) : '—',
              ],
            })),
            {
              key: 'total',
              cells: [
                <Fragment key="t">
                  <strong>Total</strong>{' '}
                  <span className="muted t-small">
                    {'+ $' + nfmt(projection.ytd) + ' YTD = ' + money(projection.projectedYear) + ' projected year'}
                  </span>
                </Fragment>,
                '',
                <strong key="p">{money(projection.total)}</strong>,
              ],
            },
          ]}
        />
      </section>

      <section className="panel-card">
        <Tabs
          id="div-tabs"
          variant="segmented"
          raised
          ariaLabel="Dividend breakdown"
          active={tab}
          style={{ margin: '4px 0 12px' }}
          onChange={setTab}
          items={[
            { key: 'monthly', label: 'By Month', pane: 'div-tab-monthly' },
            { key: 'yearly', label: 'By Year', pane: 'div-tab-yearly' },
            { key: 'tickers', label: 'By Ticker', pane: 'div-tab-tickers' },
          ]}
        />

        {tab === 'monthly' ? (
          <div id="div-tab-monthly" role="tabpanel">
            <Table
              tableClass="holdings-table reorder-off row-hover"
              columns={[{ label: 'Month' }, { label: lbl('Payments'), num: true }, { label: lbl('Taxable'), num: true }, { label: lbl('Total'), num: true }]}
              onClickRow={(k) => setOpenRow((cur) => (cur === k ? null : k))}
              rows={[...monthly].reverse().map((m) => ({
                key: m.ym,
                rowClass: 'div-month-row row-action',
                rowAttrs: { 'data-ym': m.ym, 'data-kind': 'month' },
                cells: [m.ym, m.c, money(m.taxable), money(m.s)],
                // The first month is open on arrival: a table of months whose history is invisible until you
                // happen to click is a table that looks like it has none.
                pane: {
                  open: openRow === m.ym,
                  rowClass: 'div-history-row',
                  paneClass: 'div-history',
                  rowAttrs: { 'data-ym': m.ym, 'data-kind': 'month' },
                  content: <History kind="month" id={m.ym} open={openRow === m.ym} />,
                } }))}
              empty="No dividends yet."
            />
          </div>
        ) : null}

        {tab === 'yearly' ? (
          <div id="div-tab-yearly" role="tabpanel">
            <Table
              tableClass="holdings-table reorder-off row-hover"
              columns={[{ label: 'Year' }, { label: lbl('Payments'), num: true }, { label: lbl('Taxable'), num: true }, { label: lbl('Total'), num: true }]}
              onClickRow={(k) => setOpenRow((cur) => (cur === k ? null : k))}
              rows={years.map((yr) => ({
                key: yr.y,
                rowClass: 'div-year-row row-action',
                rowAttrs: { 'data-year': yr.y, 'data-kind': 'year' },
                cells: [yr.y, yr.c, money(yr.taxable), money(yr.s)],
                pane: {
                  open: openRow === yr.y,
                  rowClass: 'div-year-history-row',
                  paneClass: 'div-year-history',
                  rowAttrs: { 'data-year': yr.y, 'data-kind': 'year' },
                  content: <History kind="year" id={yr.y} open={openRow === yr.y} />,
                },
              }))}
            />
          </div>
        ) : null}

        {tab === 'tickers' ? (
          <div id="div-tab-tickers" role="tabpanel">
            <Table
              tableClass="holdings-table reorder-off row-hover"
              columns={[
                { label: 'Ticker' },
                { label: lbl('Payments (12mo)'), num: true },
                { label: lbl('Yield on cost'), num: true },
                { label: lbl('All time'), num: true },
                { label: lbl('ROC %'), num: true },
                { label: lbl('Taxable'), num: true },
                { label: lbl('Amount'), num: true },
              ]}
              onClickRow={(k) => setOpenRow((cur) => (cur === k ? null : k))}
              rows={rows.map((r) => ({
                key: r.ticker,
                rowClass: 'div-ticker-row row-action',
                rowAttrs: { 'data-ticker': r.ticker, 'data-kind': 'ticker' },
                pane: {
                  open: openRow === r.ticker,
                  rowClass: 'div-history-row',
                  paneClass: 'div-history',
                  rowAttrs: { 'data-ticker': r.ticker, 'data-kind': 'ticker' },
                  content: <History kind="ticker" id={r.ticker} open={openRow === r.ticker} />,
                },
                cells: [
                  <strong key="t">{r.ticker}</strong>,
                  r.payments12,
                  r.yieldOnCost != null ? `${r.yieldOnCost.toFixed(2)}%` : '—',
                  money(r.allTime),
                  r.rocPct > 0 ? `${r.rocPct.toFixed(1)}%` : '0%',
                  money(r.taxable),
                  money(r.t12),
                ],
              }))}
            />
          </div>
        ) : null}
      </section>
    </main>
  );
}
